/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState } from 'react';
import { BILLING_DECIMAL_PLACES } from '../../../constants/billingConstants';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import InfoIcon from '@material-ui/icons/Info';

/**
 * Requires Review Tab Component
 * Displays billing items that require manual review with server-side pagination
 * 
 * CSV Export/Import Workflow:
 * ---------------------------
 * 1. EXPORT: Downloads CSV with all review items including:
 *    - Items missing from master sheet (need to create master + operator override)
 *    - Items missing from operator overrides (need to create operator override only)
 * 
 * 2. CSV STRUCTURE:
 *    - operatorId: Which operator this item is for
 *    - rawBillingItemName: The billing item name from Soracom
 *  - operatorId
 *  - rawBillingItemName
 *  - billingItemName (defaults to rawBillingItemName)
 *  - masterPurchasePrice, masterSalePrice (for creating new master items)
 *    - Master fields: billingItemName, displayItemName, chargeCode, unit, soracomInvoiceItemName, 
 *                     masterPurchasePrice, masterSalePrice, GSTApplicable, GSTPercentage
 *    - Operator fields: useMaster, operatorPurchasePrice, operatorSalePrice, excludeFromBilling
 * 
 * 3. USER EDITS CSV:
 *    - Fills in missing master fields (displayItemName, chargeCode, unit, prices, etc.)
 *    - Sets operator-specific pricing if useMaster=FALSE
 *    - Sets excludeFromBilling if needed
 * 
 * 4. IMPORT: Backend processes each row:
 *    - Creates master items (if action includes CREATE_MASTER)
 *    - Creates operator overrides (for all rows)
 *    - Removes items from review queue once processed
 *    - Returns summary with successes/failures
 */
const RequiresReviewTab = ({
  classes,
  reviewSearch,
  reviewOperatorFilter,
  operators,
  reviewItems,
  totalReviewCount,
  reviewPage,
  reviewPerPage,
  onReviewSearchChange,
  onReviewOperatorFilterChange,
  onReviewPageChange,
  onReviewPerPageChange,
  onOpenReviewDetail,
  onExport,
  onCsvUpload,
}) => {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  return (
    <>
      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            CSV Export/Import Guide
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            📋 Overview
          </Typography>
          <Typography paragraph>
            The CSV export/import feature allows you to bulk-process review items by downloading
            a CSV file, filling in the required information, and uploading it back. The system
            will automatically create master billing items and operator overrides.
          </Typography>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            🔄 Workflow
          </Typography>
          <Typography component="div">
            <ol>
              <li><strong>Click "Export All CSV"</strong> - Downloads all review items with existing data pre-filled</li>
              <li><strong>Open in Excel/Google Sheets</strong> - Edit the CSV file</li>
              <li><strong>Fill in required fields</strong> - Complete empty columns based on ACTION type</li>
              <li><strong>Click "Import CSV"</strong> - Upload the completed file</li>
              <li><strong>Review results</strong> - Check which items were created successfully</li>
            </ol>
          </Typography>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            📊 CSV Columns Explained
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            The CSV file contains the following columns. Required columns depend on the ACTION type.
          </Typography>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 12, color: '#1976d2' }}>
            <strong>🔹 Basic Information (Pre-filled, Do Not Modify)</strong>
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• <strong>Operator ID:</strong> Which operator this billing item is for (e.g., OP0075266100)</Typography>
            <Typography variant="body2">• <strong>Raw Billing Item Name:</strong> Original item name from Soracom's billing API (e.g., basicCharge-plan01s-active)</Typography>
            <Typography variant="body2">• <strong>Billing Item Name:</strong> Internal unique identifier - usually same as Raw Billing Item Name</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16, color: '#d32f2f' }}>
            <strong>🔹 Master Sheet Fields (Fill if this item doesn't exist in Master Sheet)</strong>
          </Typography>
          <Box ml={2} mb={1}>
            <Typography variant="caption" color="textSecondary" paragraph>
              ℹ️ The system will automatically create a master item if it doesn't exist. Fill these fields for new items:
            </Typography>
            <Typography variant="body2">• <strong>Display Name:</strong> Customer-facing name shown on invoices (e.g., "SIM Fee", "Data Usage", "Monthly Charge")</Typography>
            <Typography variant="body2">• <strong>Charge Code:</strong> Internal accounting code (e.g., CHG001, CHG002)</Typography>
            <Typography variant="body2">• <strong>Unit:</strong> Unit of measurement (e.g., DAYS, per_month, per_MB, per_device)</Typography>
            <Typography variant="body2">• <strong>Soracom Invoice Item Name:</strong> Exact name as it appears on Soracom's invoice to Thinxtra</Typography>
            <Typography variant="body2">• <strong>Master Purchase Price (USD):</strong> Default cost price in Australian Dollars (numeric only, e.g., 1.5)</Typography>
            <Typography variant="body2">• <strong>Master Sale Price (USD):</strong> Default selling price in Australian Dollars (numeric only, e.g., 2.0)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16, color: '#388e3c' }}>
            <strong>🔹 GST Tax Settings (Required for All Items)</strong>
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• <strong>GST Applicable:</strong> Must be exactly "TRUE" or "FALSE" (case-sensitive)</Typography>
            <Typography variant="body2">• <strong>GST Percentage:</strong> Tax rate as a number (e.g., 10 for 10% GST in Australia)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16, color: '#f57c00' }}>
            <strong>🔹 Operator Override Settings (Required for All Items)</strong>
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">
              • <strong>Use Master Pricing:</strong> TRUE or FALSE
              <br />
              <span style={{ marginLeft: 16, fontSize: '0.85em', color: '#666' }}>
                If TRUE → Operator uses master prices (set operator prices to 0)
                <br />
                If FALSE → Operator uses custom prices. You MUST fill in operator price fields below.
              </span>
            </Typography>
            <Typography variant="body2">• <strong>Operator Purchase Price (USD):</strong> Custom cost price for this operator (set to 0 if Use Master Pricing = TRUE)</Typography>
            <Typography variant="body2">• <strong>Operator Sale Price (USD):</strong> Custom selling price for this operator (set to 0 if Use Master Pricing = TRUE)</Typography>
            <Typography variant="body2">• <strong>Exclude From Billing:</strong> TRUE or FALSE - If TRUE, this item won't appear on this operator's invoices (use for promotional items)</Typography>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            🤖 How It Works
          </Typography>
          <Box ml={2}>
            <Typography variant="body2" paragraph>
              The system automatically determines what to create:
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>1. Master Item Check:</strong> If the billing item name doesn't exist in the Master Sheet,
              it will be created using the master fields you provide.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>2. Operator Override:</strong> An operator-specific override is always created,
              using either master prices (if Use Master Pricing = TRUE) or custom prices.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>3. Review Queue Cleanup:</strong> Successfully imported items are automatically
              removed from the review queue.
            </Typography>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            � Example CSV Row
          </Typography>
          <Paper style={{ padding: 12, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
            <Typography variant="caption" component="div" style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
              <strong>Example with Use Master Pricing = TRUE:</strong>
            </Typography>
            <Typography variant="caption" component="div" style={{ fontFamily: 'monospace', fontSize: 10 }}>
              Operator ID: OP0075266100<br />
              Raw Billing Item Name: basicCharge-plan01s-active<br />
              Billing Item Name: basicCharge-plan01s-active<br />
              Display Name: Basic Plan Charge<br />
              Charge Code: CHG001<br />
              Unit: DAYS<br />
              Soracom Invoice Item Name: Basic Charge - Plan 01s Active<br />
              Master Purchase Price (USD): 1.5<br />
              Master Sale Price (USD): 2.0<br />
              GST Applicable: TRUE<br />
              GST Percentage: 10<br />
              Use Master Pricing: TRUE<br />
              Operator Purchase Price (USD): <em>(leave empty when Use Master Pricing = TRUE)</em><br />
              Operator Sale Price (USD): <em>(leave empty when Use Master Pricing = TRUE)</em><br />
              Exclude From Billing: FALSE
            </Typography>
          </Paper>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            💡 Tips
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• If "Use Master Pricing" = TRUE, you can leave operator price fields empty</Typography>
            <Typography variant="body2">• Multiple items can share the same "Display Name" - they'll be grouped on invoices</Typography>
            <Typography variant="body2">• Set "Exclude From Billing" = TRUE for promotional items or volume discounts</Typography>
            <Typography variant="body2">• Export filters are applied - use operator filter to export specific operators only</Typography>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom color="error">
            ⚠️ Important Notes
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• <strong>Do NOT modify:</strong> "Operator ID" and "Raw Billing Item Name" columns</Typography>
            <Typography variant="body2">• <strong>Price format:</strong> All prices must be numeric only (e.g., 1.5 not $1.50 or 1.5 AUD)</Typography>
            <Typography variant="body2">• <strong>Boolean format:</strong> Must be exactly "TRUE" or "FALSE" in all caps (not true, false, Yes, No, 1, 0)</Typography>
            <Typography variant="body2">• <strong>Required fields:</strong> If creating a new master item, all Master Sheet fields must be filled</Typography>
            <Typography variant="body2">• <strong>Auto-cleanup:</strong> Successfully imported items will automatically be removed from the review queue</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} color="primary">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search item or operator…"
          value={reviewSearch}
          onChange={(e) => {
            onReviewSearchChange(e.target.value);
            onReviewPageChange(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={reviewOperatorFilter}
            onChange={(e) => {
              onReviewOperatorFilterChange(e.target.value);
              onReviewPageChange(0);
            }}
            label="Operator"
          >
            <MenuItem value="ALL">All operators</MenuItem>
            {(operators || []).map((op) => (
              <MenuItem key={op.operatorId} value={op.operatorId}>
                {op.operatorId} – {op.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Download/upload always operates on full dataset, not just current page */}
        <Tooltip title="Download ALL review items as CSV (not just this page)">
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => onExport('review')}
          >
            Export All CSV
          </Button>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PublishIcon />}
          component="label"
        >
          Import CSV
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => onCsvUpload(e, 'review')}
          />
        </Button>
        <Tooltip title="Learn how CSV Export/Import works">
          <IconButton
            size="small"
            color="primary"
            onClick={() => setHelpDialogOpen(true)}
            style={{ marginLeft: 4 }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </div>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>Billing Item Name</TableCell>
              <TableCell>Master Exists</TableCell>
              <TableCell align="right">Master Purchase $</TableCell>
              <TableCell align="right">Master Sale $</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reviewItems.map((item) => (
              <TableRow
                key={item._id}
                hover
                className={
                  item.reviewType === 'MISSING_FROM_MASTER'
                    ? classes.missingMaster
                    : classes.flagged
                }
              >
                <TableCell>
                  <Typography variant="body2">
                    <strong>{item.operatorId}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {item.operatorName}
                  </Typography>
                </TableCell>
                <TableCell>{item.rawBillingItemName}</TableCell>
                <TableCell>
                  {item.masterExists
                    ? <Chip size="small" label="Yes" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                    : <Chip size="small" label="No" style={{ backgroundColor: '#fce4ec', color: '#c62828' }} />
                  }
                </TableCell>
                <TableCell align="right">
                  {item.masterExists && item.masterPurchasePrice != null
                    ? `$${Number(item.masterPurchasePrice).toFixed(BILLING_DECIMAL_PLACES)}`
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {item.masterExists && item.masterSalePrice != null
                    ? `$${Number(item.masterSalePrice).toFixed(BILLING_DECIMAL_PLACES)}`
                    : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenReviewDetail(item)}
                  >
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalReviewCount}
          page={reviewPage}
          onPageChange={(_, p) => onReviewPageChange(p)}
          rowsPerPage={reviewPerPage}
          onRowsPerPageChange={(e) => {
            onReviewPerPageChange(+e.target.value);
            onReviewPageChange(0);
          }}
          rowsPerPageOptions={[100, 200, 500]}
        />
      </TableContainer>
    </>
  );
};

export default RequiresReviewTab;
